import type {
  ExpandDescriptionResponse,
  ExpandTechnicalDescriptionResponse,
  GenerateMilestonesResponse,
  GenerateUserStoriesResponse,
  GenerateSchemaResponse,
  TechnicalSolution,
  TechChoice,
  ArchitectureComponent,
  Integration,
} from "@/features/Projects";

export class ResponseParsers {
  /**
   * Remove comentários JavaScript de strings JSON
   */
  private static removeJavaScriptComments(jsonString: string): string {
    // Remove comentários de linha simples (//...)
    let cleaned = jsonString.replace(/\/\/.*$/gm, '');
    
    // Remove comentários de múltiplas linhas (/* ... */)
    cleaned = cleaned.replace(/\/\*[\s\S]*?\*\//g, '');
    
    // Remove linhas que contenham apenas "..." ou reticências
    cleaned = cleaned.replace(/^\s*"\.\.\..*"[,]?\s*$/gm, '');
    cleaned = cleaned.replace(/^\s*\/\/ \.\.\. .*$/gm, '');
    
    // Remove linhas em branco extras
    cleaned = cleaned.replace(/^\s*\n/gm, '');
    
    return cleaned;
  }

  static extractJsonFromResponse(response: string): any {
    
    try {
      // Método 1: Tentar extrair de bloco ```json```
      const jsonMatch = response.match(/```json\n?([\s\S]*?)\n?```/);
      if (jsonMatch) {
        const cleanedJson = this.removeJavaScriptComments(jsonMatch[1]);
        return JSON.parse(cleanedJson);
      }

      // Método 2: Procurar por { até o último } válido
      const jsonStart = response.indexOf("{");
      if (jsonStart !== -1) {
        // Procurar pelo último } que forma um JSON válido
        let jsonContent = "";
        let braceCount = 0;
        let inString = false;
        let escapeNext = false;
        
        for (let i = jsonStart; i < response.length; i++) {
          const char = response[i];
          
          if (escapeNext) {
            escapeNext = false;
            jsonContent += char;
            continue;
          }
          
          if (char === '\\' && inString) {
            escapeNext = true;
            jsonContent += char;
            continue;
          }
          
          if (char === '"' && !escapeNext) {
            inString = !inString;
          }
          
          if (!inString) {
            if (char === '{') {
              braceCount++;
            } else if (char === '}') {
              braceCount--;
            }
          }
          
          jsonContent += char;
          
          // Se chegamos ao fim do JSON principal
          if (braceCount === 0 && jsonContent.length > 1) {
            const cleanedJson = this.removeJavaScriptComments(jsonContent);
            return JSON.parse(cleanedJson);
          }
        }
        
        // Fallback: tentar o último }
        const jsonEnd = response.lastIndexOf("}") + 1;
        if (jsonEnd > jsonStart) {
          const fallbackJson = response.substring(jsonStart, jsonEnd);
          try {
            const cleanedJson = this.removeJavaScriptComments(fallbackJson);
            return JSON.parse(cleanedJson);
          } catch (fallbackError) {
          }
        }
      }

      // Método 3: Tentar a resposta completa
      const cleanedResponse = this.removeJavaScriptComments(response);
      return JSON.parse(cleanedResponse);
      
    } catch (error) {
      console.error("❌ Erro ao extrair JSON da resposta:", error);
      throw new Error("Resposta da IA não está em formato JSON válido");
    }
  }

  static cleanResponse(response: string): string {
    let cleanResponse = response.trim();

    if (cleanResponse.startsWith("```json")) {
      cleanResponse = cleanResponse
        .replace(/^```json\n?/, "")
        .replace(/\n?```$/, "");
    }
    if (cleanResponse.startsWith("```")) {
      cleanResponse = cleanResponse
        .replace(/^```\n?/, "")
        .replace(/\n?```$/, "");
    }

    return cleanResponse;
  }

  static parseExpandDescriptionResponse(response: string): ExpandDescriptionResponse {
    try {
      const cleanResponse = this.cleanResponse(response);
      const jsonMatch = cleanResponse.match(/\{[\s\S]*\}/);
      
      if (!jsonMatch) {
        throw new Error("JSON não encontrado na resposta");
      }

      const parsed = JSON.parse(jsonMatch[0]);

      if (!parsed.expandedDescription) {
        throw new Error("Descrição expandida não encontrada");
      }

      return {
        expandedDescription: parsed.expandedDescription,
        suggestedName: parsed.suggestedName || undefined,
        estimatedComplexity: parsed.estimatedComplexity || "medium",
        keyFeatures: parsed.keyFeatures || [],
        targetUsers: parsed.targetUsers || [],
        technicalConsiderations: parsed.technicalConsiderations || [],
        businessValue: parsed.businessValue || undefined,
        marketContext: parsed.marketContext || undefined,
        riskFactors: parsed.riskFactors || [],
        successMetrics: parsed.successMetrics || [],
      };
    } catch (error) {
      console.error("Erro ao parsear resposta do Gemini:", error);

      return {
        expandedDescription: response,
        estimatedComplexity: "medium",
      };
    }
  }

  static parseObjectivesResponse(response: string): string[] {
    try {
      const cleanResponse = this.cleanResponse(response);
      const jsonMatch = cleanResponse.match(/\[[\s\S]*\]/);
      
      if (!jsonMatch) {
        throw new Error("Lista de objetivos não encontrada");
      }

      return JSON.parse(jsonMatch[0]);
    } catch (error) {
      console.error("Erro ao parsear objetivos:", error);
      throw new Error("Falha ao processar objetivos gerados pela IA");
    }
  }

  static parseTechnicalSolutionResponse(response: string): ExpandTechnicalDescriptionResponse {
    try {
      const parsed = this.extractJsonFromResponse(response);

      if (!parsed.expandedDescription && !parsed.expandedTechnicalDescription) {
        throw new Error("Descrição técnica expandida não encontrada");
      }

      return {
        expandedTechnicalDescription: parsed.expandedTechnicalDescription || parsed.expandedDescription,
        suggestedTechnologies: parsed.suggestedTechnologies || [],
        estimatedComplexity: parsed.estimatedComplexity || parsed.technicalComplexity || "medium",
      };
    } catch (error) {
      console.error("Erro ao parsear solução técnica:", error);

      return {
        expandedTechnicalDescription: response,
        estimatedComplexity: "medium",
      };
    }
  }

  static parseFullTechnicalSolutionResponse(response: string): TechnicalSolution {
    try {
      const parsed = this.extractJsonFromResponse(response);

      // Adaptador inteligente: Converte resposta da IA para interface TechnicalSolution
      // A IA pode retornar em formatos diferentes, adaptamos para nossa interface
      
      const adaptedSolution: TechnicalSolution = {
        description: parsed.expandedDescription || parsed.description || "Descrição técnica gerada",
        
        recommendedStack: {
          frontend: this.adaptTechArray(parsed.recommendedTechnologies?.frontend || []),
          backend: this.adaptTechArray(parsed.recommendedTechnologies?.backend || []),
          database: this.adaptTechArray(parsed.recommendedTechnologies?.database || []),
          infrastructure: this.adaptTechArray(parsed.recommendedTechnologies?.infrastructure || []),
          devops: this.adaptTechArray([
            ...(parsed.recommendedTechnologies?.devops || []),
            ...(parsed.recommendedTechnologies?.authentication || []),
            ...(parsed.recommendedTechnologies?.mobile || [])
          ])
        },
        
        architecture: {
          pattern: parsed.suggestedArchitecture || parsed.designPatterns?.[0] || "Arquitetura Modular",
          description: parsed.suggestedArchitecture || "Arquitetura escalável e maintível",
          components: this.generateComponentsFromTechnologies(parsed.recommendedTechnologies || {}),
          integrations: this.generateBasicIntegrations()
        },
        
        considerations: {
          performance: parsed.nonFunctionalRequirements?.performance ? [parsed.nonFunctionalRequirements.performance] : [],
          security: parsed.nonFunctionalRequirements?.security ? [parsed.nonFunctionalRequirements.security] : [],
          scalability: parsed.nonFunctionalRequirements?.scalability ? [parsed.nonFunctionalRequirements.scalability] : [],
          maintenance: parsed.nonFunctionalRequirements?.maintainability || parsed.nonFunctionalRequirements?.availability ? 
            [parsed.nonFunctionalRequirements.maintainability || parsed.nonFunctionalRequirements.availability] : []
        },
        
        complexity: {
          overall: parsed.technicalComplexity === "high" ? "high" : 
                   parsed.technicalComplexity === "low" ? "low" : "medium",
          frontend: parsed.technicalComplexity === "high" ? 8 : parsed.technicalComplexity === "low" ? 3 : 5,
          backend: parsed.technicalComplexity === "high" ? 8 : parsed.technicalComplexity === "low" ? 3 : 5,
          devops: parsed.technicalComplexity === "high" ? 8 : parsed.technicalComplexity === "low" ? 3 : 5
        },
        
        justifications: this.extractJustifications(parsed.recommendedTechnologies || {}),
        
        // Novos campos dos prompts melhorados
        designPatterns: parsed.designPatterns || [],
        nonFunctionalRequirements: parsed.nonFunctionalRequirements ? {
          performance: parsed.nonFunctionalRequirements.performance || "",
          security: parsed.nonFunctionalRequirements.security || "",
          scalability: parsed.nonFunctionalRequirements.scalability || "",
          availability: parsed.nonFunctionalRequirements.availability || "",
          maintainability: parsed.nonFunctionalRequirements.maintainability || ""
        } : undefined,
        technicalRisks: parsed.technicalRisks || [],
        implementationStrategy: parsed.implementationStrategy || undefined,
        developmentPhases: parsed.developmentPhases || [],
        teamRequirements: parsed.teamRequirements || undefined
      };

      return adaptedSolution;

    } catch (error) {
      console.error("❌ Erro ao adaptar resposta da IA:", error);
      throw new Error("Falha ao processar resposta da IA para solução técnica.");
    }
  }

  // Método auxiliar para adaptar arrays de tecnologias
  private static adaptTechArray(techArray: any[]): TechChoice[] {
    return techArray.map(tech => ({
      name: tech.name || String(tech),
      version: tech.version || "",
      justification: tech.justification || "",
      alternatives: tech.alternatives || []
    }));
  }

  // Método auxiliar para gerar componentes básicos baseados nas tecnologias
  private static generateComponentsFromTechnologies(technologies: any): ArchitectureComponent[] {
    const components: ArchitectureComponent[] = [];
    let componentId = 1;

    if (technologies.frontend?.length > 0) {
      components.push({
        id: `component-${componentId++}`,
        name: "Frontend Application",
        type: "frontend",
        description: `Interface desenvolvida com ${technologies.frontend.map((t: any) => t.name || t).join(', ')}`,
        technologies: technologies.frontend.map((t: any) => t.name || String(t)),
        connections: ["component-2"]
      });
    }

    if (technologies.backend?.length > 0) {
      components.push({
        id: `component-${componentId++}`,
        name: "Backend Services",
        type: "backend",
        description: `API e lógica de negócio com ${technologies.backend.map((t: any) => t.name || t).join(', ')}`,
        technologies: technologies.backend.map((t: any) => t.name || String(t)),
        connections: ["component-3"]
      });
    }

    if (technologies.database?.length > 0) {
      components.push({
        id: `component-${componentId++}`,
        name: "Data Layer",
        type: "database",
        description: `Persistência com ${technologies.database.map((t: any) => t.name || t).join(', ')}`,
        technologies: technologies.database.map((t: any) => t.name || String(t)),
        connections: []
      });
    }

    return components;
  }

  // Método auxiliar para gerar integrações básicas
  private static generateBasicIntegrations(): Integration[] {
    return [
      {
        id: "integration-1",
        name: "API Communication",
        type: "api",
        description: "Comunicação entre frontend e backend via API RESTful",
        source: "component-1",
        target: "component-2"
      }
    ];
  }

  // Método auxiliar para extrair justificativas
  private static extractJustifications(technologies: any): { [techName: string]: string } {
    const justifications: { [techName: string]: string } = {};
    
    Object.values(technologies).flat().forEach((tech: any) => {
      if (tech?.name && tech?.justification) {
        justifications[tech.name] = tech.justification;
      }
    });
    
    return justifications;
  }

  static parseMilestonesResponse(response: string): GenerateMilestonesResponse {
    try {
      const parsed = this.extractJsonFromResponse(response);

      if (!parsed.milestones || !Array.isArray(parsed.milestones)) {
        throw new Error("Lista de milestones não encontrada");
      }

      const validatedMilestones = parsed.milestones.map((milestone: any, index: number) => {
        if (!milestone.title || !milestone.description) {
          throw new Error(`Milestone ${index + 1} incompleto`);
        }

        return {
          id: milestone.id || `milestone-${Date.now()}-${index}`,
          title: milestone.title,
          description: milestone.description,
          estimatedDuration: milestone.estimatedDuration || 7,
          dependencies: milestone.dependencies || [],
          phase: "milestones" as const,
          deliverables: milestone.deliverables || [],
          startDate: milestone.startDate || undefined,
          endDate: milestone.endDate || milestone.dueDate || undefined,
          priority: milestone.priority || "medium",
          status: "not_started" as const,
        };
      });

      return {
        timeline: {
          milestones: validatedMilestones,
          totalDuration: validatedMilestones.reduce((sum: number, m: any) => sum + m.estimatedDuration, 0),
          criticalPath: validatedMilestones.map((m: any) => m.id),
          startDate: validatedMilestones[0]?.startDate,
          endDate: validatedMilestones[validatedMilestones.length - 1]?.endDate,
        }
      };
    } catch (error) {
      console.error("Erro ao parsear milestones:", error);
      throw new Error("Falha ao processar milestones gerados pela IA");
    }
  }

  static parseUserStoriesResponse(response: string): GenerateUserStoriesResponse {
    try {
      const parsed = this.extractJsonFromResponse(response);

      if (!parsed.epics || !Array.isArray(parsed.epics)) {
        throw new Error("Lista de épicos não encontrada");
      }

      let allStoriesTemp: any[] = [];

      const validatedEpics = parsed.epics.map((epic: any, epicIndex: number) => {
        if (!epic.id || !epic.title || !epic.stories) {
          throw new Error(`Épico ${epicIndex + 1} incompleto`);
        }

        const validatedStories = epic.stories.map((story: any, storyIndex: number) => {
          if (!story.id || !story.title || !story.description) {
            throw new Error(`Story ${storyIndex + 1} do épico ${epic.title} incompleta`);
          }
          

          return {
            id: story.id || `story-${epicIndex}-${storyIndex}-${Date.now()}`,
            title: story.title,
            description: story.description,
            
            // Campos obrigatórios da interface UserStory
            role: story.role && story.role.trim() ? story.role : "",
            functionality: story.functionality && story.functionality.trim() ? story.functionality : "",
            benefit: story.benefit && story.benefit.trim() ? story.benefit : "",
            
            priority: story.priority === "high" || story.priority === "must_have" ? "must_have" : 
                     story.priority === "medium" || story.priority === "should_have" ? "should_have" : 
                     story.priority === "low" || story.priority === "could_have" ? "could_have" : "should_have",
            status: "backlog" as const,
            type: story.type === "bug" ? "bug" : 
                  story.type === "technical" ? "technical" : 
                  story.type === "enhancement" ? "enhancement" : 
                  story.type === "feature" ? "feature" : "feature",
            
            storyPoints: story.storyPoints || 1,
            estimatedHours: story.estimatedHours || (story.storyPoints || 1) * 8,
            
            // Critérios de aceitação
            acceptanceCriteria: (story.acceptanceCriteria || []).map((criteria: any, index: number) => ({
              id: `criteria-${story.id || `story-${epicIndex}-${storyIndex}`}-${index}-${Date.now()}`,
              description: typeof criteria === 'string' ? criteria : 
                          (criteria?.description || 
                           criteria?.text || 
                           criteria?.criterion || 
                           `Critério de aceitação ${index + 1}`),
              completed: false
            })),
            
            // Metadados
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            
            // Campos opcionais para compatibilidade
            epic: epic.id,
            dependencies: story.dependencies || [],
            notes: story.notes || "",
            tags: story.tags || []
          };
        });

        // Adicionar stories ao array temporário
        allStoriesTemp = allStoriesTemp.concat(validatedStories);

        return {
          id: epic.id,
          title: epic.title,
          description: epic.description || "",
          color: epic.color || "#6366f1", // Default indigo color
          priority: epic.priority === "high" || epic.priority === "must_have" ? "must_have" : 
                   epic.priority === "medium" || epic.priority === "should_have" ? "should_have" : 
                   epic.priority === "low" || epic.priority === "could_have" ? "could_have" : "should_have",
          estimatedStoryPoints: validatedStories.reduce((sum: number, story: any) => sum + (story.storyPoints || 0), 0),
          stories: validatedStories.map((s: any) => s.id), // IDs para referência nos épicos
        };
      });

      const allStories = allStoriesTemp;
      const currentTime = new Date().toISOString();
      
      const finalResult = {
        stories: {
          epics: validatedEpics,
          stories: allStories,
          totalStoryPoints: allStories.reduce((sum: number, story: any) => sum + (story.storyPoints || 0), 0),
          totalEstimatedHours: allStories.reduce((sum: number, story: any) => sum + (story.estimatedHours || 0), 0),
          createdAt: currentTime,
          updatedAt: currentTime,
        }
      };
      
      return finalResult;
    } catch (error) {
      console.error("Erro ao parsear user stories:", error);
      throw new Error("Falha ao processar user stories geradas pela IA");
    }
  }

  static parseSchemaResponse(response: string): GenerateSchemaResponse {
    try {
      const parsed = this.extractJsonFromResponse(response);

      if (!parsed.dataModels || !parsed.apiEndpoints) {
        console.error("❌ Schema incompleto:", { 
          hasDataModels: !!parsed.dataModels, 
          hasApiEndpoints: !!parsed.apiEndpoints 
        });
        throw new Error("Schema incompleto - modelos ou endpoints não encontrados");
      }

      const currentTime = new Date().toISOString();

      const validatedDataModels = parsed.dataModels.map((model: any) => ({
        id: model.id || `model-${Date.now()}-${Math.random()}`,
        name: model.name,
        description: model.description || "",
        tableName: model.tableName || model.name?.toLowerCase(),
        fields: model.fields || [],
        relationships: model.relationships || [],
        timestamps: model.timestamps !== false,
        softDelete: model.softDelete || false,
        createdAt: model.createdAt || currentTime,
        updatedAt: model.updatedAt || currentTime,
      }));

      const validatedApiEndpoints = parsed.apiEndpoints.map((endpoint: any) => ({
        id: endpoint.id || `endpoint-${Date.now()}-${Math.random()}`,
        path: endpoint.path,
        method: endpoint.method,
        summary: endpoint.summary || "",
        description: endpoint.description || "",
        parameters: endpoint.parameters || [],
        responses: endpoint.responses || [],
        tags: endpoint.tags || [],
        requiresAuth: endpoint.requiresAuth || false,
        createdAt: endpoint.createdAt || currentTime,
        updatedAt: endpoint.updatedAt || currentTime,
      }));

      const totalFields = validatedDataModels.reduce(
        (total: number, model: any) => total + (model.fields?.length || 0),
        0
      );

      const finalSchema = {
        schema: {
          dataModels: validatedDataModels,
          apiEndpoints: validatedApiEndpoints,
          apiGroups: parsed.apiGroups || [],
          documentation: parsed.documentation || [],
          config: parsed.config || {
            apiVersion: "v1",
            baseUrl: "https://api.exemplo.com",
            databaseType: "postgresql",
            authenticationMethod: "jwt",
            corsEnabled: true,
            rateLimitEnabled: true,
            loggingEnabled: true,
            openApiInfo: {
              title: "API do Projeto",
              description: "API gerada automaticamente",
              version: "1.0.0",
            },
          },
          stats: {
            totalModels: validatedDataModels.length,
            totalEndpoints: validatedApiEndpoints.length,
            totalFields,
            complexity: "medium" as const,
          },
          createdAt: currentTime,
          updatedAt: currentTime,
        }
      };
      
      
      return finalSchema;
    } catch (error) {
      console.error("Erro ao parsear schema:", error);
      throw new Error("Falha ao processar schema gerado pela IA");
    }
  }

  static parseDataModelsResponse(response: string): any[] {
    try {
      const cleanResponse = this.cleanResponse(response);
      let parsed: any;

      const arrayMatch = cleanResponse.match(/\[[\s\S]*\]/);
      if (arrayMatch) {
        parsed = JSON.parse(arrayMatch[0]);
      } else {
        parsed = JSON.parse(cleanResponse);
      }

      if (!Array.isArray(parsed)) {
        throw new Error("Resposta não é um array de modelos");
      }

      const currentTime = new Date().toISOString();

      return parsed.map((model: any) => ({
        id: model.id || `model-${Date.now()}-${Math.random()}`,
        name: model.name,
        description: model.description || "",
        tableName: model.tableName || model.name?.toLowerCase(),
        fields: model.fields || [],
        relationships: model.relationships || [],
        timestamps: model.timestamps !== false,
        softDelete: model.softDelete || false,
        createdAt: model.createdAt || currentTime,
        updatedAt: model.updatedAt || currentTime,
      }));
    } catch (error) {
      console.error("Erro ao parsear modelos de dados:", error);
      throw new Error("Falha ao processar modelos gerados pela IA");
    }
  }

  static parseApiEndpointsResponse(response: string): any[] {
    try {
      const cleanResponse = this.cleanResponse(response);
      let parsed: any;

      // Primeiro, tentar encontrar um array válido na resposta
      const arrayMatch = cleanResponse.match(/\[[\s\S]*\]/);
      if (arrayMatch) {
        let arrayString = arrayMatch[0];
        
        // Se o JSON está incompleto, tentar "reparar" removendo a última entrada incompleta
        if (!this.isValidJson(arrayString)) {
          console.warn("⚠️ Resposta JSON incompleta detectada, tentando reparar...");
          arrayString = this.repairIncompleteJsonArray(arrayString);
        }
        
        parsed = JSON.parse(arrayString);
      } else {
        parsed = JSON.parse(cleanResponse);
      }

      if (!Array.isArray(parsed)) {
        throw new Error("Resposta não é um array de endpoints");
      }

      const currentTime = new Date().toISOString();

      return parsed.map((endpoint: any) => ({
        id: endpoint.id || `endpoint-${Date.now()}-${Math.random()}`,
        path: endpoint.path,
        method: endpoint.method,
        summary: endpoint.summary || "",
        description: endpoint.description || "",
        parameters: endpoint.parameters || [],
        responses: endpoint.responses || [],
        tags: endpoint.tags || [],
        requiresAuth: endpoint.requiresAuth || false,
        createdAt: endpoint.createdAt || currentTime,
        updatedAt: endpoint.updatedAt || currentTime,
      }));
    } catch (error) {
      console.error("Erro ao parsear endpoints de API:", error);
      console.error("Response length:", response.length);
      console.error("Response preview:", response.substring(0, 1000) + "...");
      throw new Error("Falha ao processar endpoints gerados pela IA");
    }
  }

  private static isValidJson(jsonString: string): boolean {
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  }

  private static repairIncompleteJsonArray(jsonString: string): string {
    try {
      // Remove o último objeto incompleto
      const lastCommaIndex = jsonString.lastIndexOf(',');
      const lastCompleteObjectIndex = jsonString.lastIndexOf('}');
      
      if (lastCompleteObjectIndex > lastCommaIndex) {
        // Caso o último objeto esteja completo
        return jsonString.substring(0, lastCompleteObjectIndex + 1) + ']';
      } else if (lastCommaIndex > 0) {
        // Remove o último objeto incompleto após a vírgula
        return jsonString.substring(0, lastCommaIndex) + ']';
      } else {
        // Fallback: tentar encontrar o último objeto completo
        const objects = jsonString.substring(1, jsonString.length - 1).split(/},\s*{/);
        const completeObjects = objects.slice(0, -1); // Remove o último (incompleto)
        
        if (completeObjects.length === 0) {
          return '[]'; // Array vazio se não há objetos completos
        }
        
        const repairedJson = '[' + completeObjects.join('},{') + '}]';
        return repairedJson;
      }
    } catch (error) {
      console.warn("⚠️ Não foi possível reparar o JSON, retornando array vazio");
      return '[]';
    }
  }

  static parseResourcesResponse(response: string): any {
    try {
      const cleanResponse = this.cleanResponse(response);
      return this.extractJsonFromResponse(cleanResponse);
    } catch (error) {
      console.error("Erro ao fazer parse da resposta de recursos:", error);
      
      // Fallback: estrutura básica para recursos
      return {
        resources: [],
        estimatedPrice: 0,
        suggestions: {
          team_composition: "Erro ao processar resposta da IA",
          cost_breakdown: "Não foi possível calcular custos",
          timeline_considerations: "Informações indisponíveis"
        }
      };
    }
  }

  static parseCostEstimationResponse(response: string): any {
    try {
      const cleanResponse = this.cleanResponse(response);
      return this.extractJsonFromResponse(cleanResponse);
    } catch (error) {
      console.error("Erro ao fazer parse da resposta de estimativa de custos:", error);
      
      // Fallback: estrutura básica para estimativa
      return {
        estimatedHours: 0,
        estimatedCost: 0,
        breakdown: {
          developer: { hours: 0, cost: 0 },
          designer: { hours: 0, cost: 0 },
          pm: { hours: 0, cost: 0 },
          qa: { hours: 0, cost: 0 },
          devops: { hours: 0, cost: 0 }
        }
      };
    }
  }

  static parseTeamOptimizationResponse(response: string): any {
    try {
      const cleanResponse = this.cleanResponse(response);
      return this.extractJsonFromResponse(cleanResponse);
    } catch (error) {
      console.error("Erro ao fazer parse da resposta de otimização de equipe:", error);
      
      // Fallback: estrutura básica para otimização
      return {
        optimizedTeam: [],
        reasoning: "Erro ao processar resposta da IA",
        costSavings: 0
      };
    }
  }
}